let installed = false;

const capturePassiveFalse: AddEventListenerOptions = {
  capture: true,
  passive: false,
};

/** Tunables: widen the gap between “normal horizontal scroll” and “treat as swipe / block”. */
const TOUCH_MIN_HORIZONTAL_PX = 22;
/** |dx| must be at least this times |dy| to count as horizontal-dominant. */
const TOUCH_HORIZONTAL_DOMINANCE_RATIO = 2.25;
/**
 * Inside a horizontal scroller, within this many px of min/max scrollLeft, require a
 * larger |dx| before blocking (overscroll / diagonal noise at the strip ends).
 */
const TOUCH_SCROLL_EDGE_SLOP_PX = 20;
/** When at scroll edge inside a scroller, need at least this |dx| to intercept. */
const TOUCH_AT_SCROLL_EDGE_MIN_DX = 40;

const WHEEL_MIN_DELTA_X = 6;
const WHEEL_HORIZONTAL_DOMINANCE_RATIO = 1.85;

/** Dispatched on horizontal swipe / horizontal wheel when the gesture is blocked. */
export const NAV_GUARD_SWIPE_EVENT = "mgm-nav-guard-swipe";

export type NavGuardSwipeDetail = {
  /** Positive dx / deltaX → typical “back” edge swipe; negative → forward. */
  intent: "back" | "forward";
};

function dispatchSwipeIntent(intent: "back" | "forward"): void {
  window.dispatchEvent(
    new CustomEvent<NavGuardSwipeDetail>(NAV_GUARD_SWIPE_EVENT, {
      detail: { intent },
    }),
  );
}

function eventTargetNode(e: Event): Node | null {
  const path = e.composedPath();
  const first = path[0];
  if (first instanceof Node) return first;
  return e.target instanceof Node ? e.target : null;
}

/**
 * Nearest ancestor that can scroll horizontally (overflow + overflowable width).
 */
function findHorizontalScrollContainer(start: Node | null): HTMLElement | null {
  let el: Element | null =
    start instanceof Element ? start : start?.parentElement ?? null;
  while (el && el !== document.documentElement) {
    if (el instanceof HTMLElement) {
      const s = window.getComputedStyle(el);
      const ox = s.overflowX;
      if (
        (ox === "auto" || ox === "scroll" || ox === "overlay") &&
        el.scrollWidth > el.clientWidth + 1
      ) {
        return el;
      }
    }
    el = el.parentElement;
  }
  return null;
}

/**
 * Whether horizontal input in this direction can still change scrollLeft (real
 * horizontal scroll). Uses a 1px probe and restores scroll — works across LTR/RTL.
 */
function horizontalScrollStillConsumable(
  scrollParent: HTMLElement,
  scrollLeftDelta: number,
): boolean {
  if (scrollParent.scrollWidth <= scrollParent.clientWidth + 1) return false;
  if (scrollLeftDelta === 0) return false;
  const step = scrollLeftDelta > 0 ? 1 : -1;
  const before = scrollParent.scrollLeft;
  scrollParent.scrollLeft += step;
  const changed = scrollParent.scrollLeft !== before;
  scrollParent.scrollLeft = before;
  return changed;
}

function isMostlyHorizontalTouch(dx: number, dy: number): boolean {
  if (Math.abs(dx) < TOUCH_MIN_HORIZONTAL_PX) return false;
  if (Math.abs(dx) < Math.abs(dy) * TOUCH_HORIZONTAL_DOMINANCE_RATIO) return false;
  return true;
}

/** Touch/pointer: finger moves right (dx>0) → scrollLeft decreases. */
function touchHorizontalConsumedByScrollParent(
  scrollParent: HTMLElement,
  dx: number,
): boolean {
  if (horizontalScrollStillConsumable(scrollParent, dx > 0 ? -1 : 1)) {
    return true;
  }
  const max = Math.max(0, scrollParent.scrollWidth - scrollParent.clientWidth);
  if (max <= 1) return false;
  const sl = scrollParent.scrollLeft;
  const absDx = Math.abs(dx);
  if (
    dx > 0 &&
    sl <= TOUCH_SCROLL_EDGE_SLOP_PX &&
    absDx < TOUCH_AT_SCROLL_EDGE_MIN_DX
  ) {
    return true;
  }
  if (
    dx < 0 &&
    sl >= max - TOUCH_SCROLL_EDGE_SLOP_PX &&
    absDx < TOUCH_AT_SCROLL_EDGE_MIN_DX
  ) {
    return true;
  }
  return false;
}

function isMostlyHorizontalWheel(deltaX: number, deltaY: number): boolean {
  if (Math.abs(deltaX) < WHEEL_MIN_DELTA_X) return false;
  if (Math.abs(deltaX) < Math.abs(deltaY) * WHEEL_HORIZONTAL_DOMINANCE_RATIO) {
    return false;
  }
  return true;
}

/**
 * Blocks browser-style horizontal swipe / overscroll navigation only when the
 * gesture is not consumed by normal horizontal scrolling in a scrollable region.
 *
 * If a touch/pointer **starts** inside a horizontal scroller, the whole gesture
 * is left to the browser (scroll and overscroll) — no preventDefault, no dialog.
 *
 * Wheel whose target lies inside a horizontal scroller: same — fully native.
 * Swipe / horizontal wheel **outside** those regions can still trigger the guard.
 */
export function installGlobalSwipeBlock(): void {
  if (installed || typeof document === "undefined") return;
  installed = true;

  const root = document.documentElement;

  let touchStartX = 0;
  let touchStartY = 0;
  let touchSwipePrompted = false;
  /** Horizontal scroller under the finger at touchstart; entire gesture stays “scroll context”. */
  let touchHorizontalOrigin: HTMLElement | null = null;

  root.addEventListener(
    "touchstart",
    (e: TouchEvent) => {
      if (e.touches.length !== 1) return;
      touchStartX = e.touches[0].clientX;
      touchStartY = e.touches[0].clientY;
      touchSwipePrompted = false;
      touchHorizontalOrigin = findHorizontalScrollContainer(eventTargetNode(e));
    },
    { capture: true, passive: true },
  );

  root.addEventListener(
    "touchmove",
    (e: TouchEvent) => {
      if (e.touches.length !== 1) return;
      const x = e.touches[0].clientX;
      const y = e.touches[0].clientY;
      const dx = x - touchStartX;
      const dy = y - touchStartY;
      if (!isMostlyHorizontalTouch(dx, dy)) return;

      if (touchHorizontalOrigin) {
        return;
      }

      const scrollParent = findHorizontalScrollContainer(eventTargetNode(e));
      if (
        scrollParent &&
        touchHorizontalConsumedByScrollParent(scrollParent, dx)
      ) {
        return;
      }

      e.preventDefault();
      if (!touchSwipePrompted) {
        touchSwipePrompted = true;
        dispatchSwipeIntent(dx > 0 ? "back" : "forward");
      }
    },
    capturePassiveFalse,
  );

  root.addEventListener(
    "touchend",
    () => {
      touchSwipePrompted = false;
      touchHorizontalOrigin = null;
    },
    { capture: true, passive: true },
  );
  root.addEventListener(
    "touchcancel",
    () => {
      touchSwipePrompted = false;
      touchHorizontalOrigin = null;
    },
    { capture: true, passive: true },
  );

  let ptrActive = false;
  let ptrStartX = 0;
  let ptrStartY = 0;
  let ptrSwipePrompted = false;
  let ptrHorizontalOrigin: HTMLElement | null = null;

  root.addEventListener(
    "pointerdown",
    (e: PointerEvent) => {
      if (e.pointerType !== "touch" && e.pointerType !== "pen") return;
      ptrActive = true;
      ptrStartX = e.clientX;
      ptrStartY = e.clientY;
      ptrSwipePrompted = false;
      ptrHorizontalOrigin = findHorizontalScrollContainer(eventTargetNode(e));
    },
    { capture: true, passive: true },
  );

  root.addEventListener(
    "pointermove",
    (e: PointerEvent) => {
      if (!ptrActive) return;
      if (e.pointerType !== "touch" && e.pointerType !== "pen") return;
      const dx = e.clientX - ptrStartX;
      const dy = e.clientY - ptrStartY;
      if (!isMostlyHorizontalTouch(dx, dy)) return;

      if (ptrHorizontalOrigin) {
        return;
      }

      const scrollParent = findHorizontalScrollContainer(eventTargetNode(e));
      if (
        scrollParent &&
        touchHorizontalConsumedByScrollParent(scrollParent, dx)
      ) {
        return;
      }

      e.preventDefault();
      if (!ptrSwipePrompted) {
        ptrSwipePrompted = true;
        dispatchSwipeIntent(dx > 0 ? "back" : "forward");
      }
    },
    capturePassiveFalse,
  );

  const endPtr = () => {
    ptrActive = false;
    ptrSwipePrompted = false;
    ptrHorizontalOrigin = null;
  };
  root.addEventListener("pointerup", endPtr, { capture: true, passive: true });
  root.addEventListener("pointercancel", endPtr, {
    capture: true,
    passive: true,
  });

  let wheelBurstPrompted = false;
  let wheelBurstTimer: ReturnType<typeof setTimeout> | null = null;

  root.addEventListener(
    "wheel",
    (e: WheelEvent) => {
      if (e.ctrlKey) return;
      if (!isMostlyHorizontalWheel(e.deltaX, e.deltaY)) return;

      const scrollParent = findHorizontalScrollContainer(eventTargetNode(e));
      if (scrollParent) {
        return;
      }

      e.preventDefault();
      if (!wheelBurstPrompted) {
        wheelBurstPrompted = true;
        dispatchSwipeIntent(e.deltaX > 0 ? "back" : "forward");
      }
      if (wheelBurstTimer) clearTimeout(wheelBurstTimer);
      wheelBurstTimer = setTimeout(() => {
        wheelBurstPrompted = false;
        wheelBurstTimer = null;
      }, 450);
    },
    capturePassiveFalse,
  );
}
